const IDENTITY_FIELDS = Object.freeze([
  'schema_version', 'scope', 'session_id', 'run_id', 'turn_id', 'decision_id',
  'interaction_id', 'expected_context_revision', 'input_contract_digest',
]);
const RESPONSE_FIELDS = new Set(['inputRequest', 'values', 'response_kind', 'message']);
const staleRequest = () => Object.assign(new Error('Input request is not current'), { code: 'input_request_stale' });
const assertText = (value) => {
  if (typeof value !== 'string' || !value.trim()) throw new TypeError('Input identity must be nonempty text');
};
const assertRecord = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new TypeError('Input response must be an object');
};
const assertJSONRecord = (value, ancestors, visit) => {
  const prototype = Object.getPrototypeOf(value);
  if (!Array.isArray(value) && prototype !== Object.prototype && prototype !== null) {
    throw new TypeError('Input response must contain JSON data');
  }
  if (ancestors.has(value) || Object.getOwnPropertySymbols(value).length) throw new TypeError('Input response must contain JSON data');
  ancestors.add(value);
  Object.values(Object.getOwnPropertyDescriptors(value)).forEach((descriptor) => {
    if (!Object.hasOwn(descriptor, 'value')) throw new TypeError('Input response must not contain accessors');
    visit(descriptor.value, ancestors);
  });
  ancestors.delete(value);
};
const assertJSONValue = (value, ancestors = new Set()) => {
  if (value === null) return;
  if (typeof value === 'object') return assertJSONRecord(value, ancestors, assertJSONValue);
  if (['string', 'boolean'].includes(typeof value)) return;
  if (typeof value === 'number' && Number.isFinite(value)) return;
  throw new TypeError('Input response must contain JSON data');
};
const assertRequestIdentity = (request) => {
  if (request.schema_version !== 1 || request.scope !== 'auto') throw new TypeError('Unsupported input request schema');
  if (!Number.isSafeInteger(request.expected_context_revision) || request.expected_context_revision < 0) {
    throw new TypeError('Input context revision must be nonnegative');
  }
  if (typeof request.input_contract_digest !== 'string' || !/^sha256:[0-9a-f]{64}$/.test(request.input_contract_digest)) {
    throw new TypeError('Invalid input contract digest');
  }
};
const assertRequiredFields = ({ missing_inputs, parameters }) => {
  if (!Array.isArray(missing_inputs) || !missing_inputs.length || !Array.isArray(parameters)) {
    throw new TypeError('Input request must declare required fields');
  }
  missing_inputs.forEach(assertText);
  if (new Set(missing_inputs).size !== missing_inputs.length || parameters.length !== missing_inputs.length
    || parameters.some((parameter, index) => parameter?.field_key !== missing_inputs[index] || parameter.required !== true)) {
    throw new TypeError('Input parameters must match unique required fields');
  }
};
const assertRequest = (request) => {
  assertRecord(request);
  ['session_id', 'run_id', 'turn_id', 'decision_id', 'interaction_id'].forEach((key) => assertText(request[key]));
  assertRequestIdentity(request);
  assertRequiredFields(request);
};
const captureValues = (values, fieldKeys) => {
  if (!Array.isArray(values) || !values.length || values.length > 64) throw new TypeError('Input values must be an ordered array');
  values.forEach((entry) => {
    assertRecord(entry);
    assertText(entry.field_key);
    if (!Object.hasOwn(entry, 'value') || Object.keys(entry).some((key) => !['field_key', 'value'].includes(key))) {
      throw new TypeError('Input value must contain only field_key and value');
    }
  });
  const byKey = new Map(values.map(({ field_key, value }) => [field_key, value]));
  if (byKey.size !== values.length || values.length !== fieldKeys.length || fieldKeys.some((key) => !byKey.has(key))) {
    throw new TypeError('Input values must match the current required fields');
  }
  return structuredClone(fieldKeys.map((field_key) => ({ field_key, value: byKey.get(field_key) })));
};

const RESPONSE_BUILDERS = new Map([
  ['answers', (input, fieldKeys) => {
    if (Object.hasOwn(input, 'message')) throw new TypeError('Answers cannot contain a free message');
    return { values: captureValues(input.values, fieldKeys) };
  }],
  ['message', (input) => {
    assertText(input.message);
    if (Object.hasOwn(input, 'values')) throw new TypeError('Free message cannot contain field answers');
    return { response_kind: 'message', message: input.message, values: [] };
  }],
]);
const RESPONSE_ECHO = new Map([
  ['answers', () => ({ content: '', suppressUserEcho: true })],
  ['message', (input) => ({ content: input.message, suppressUserEcho: false })],
]);

/** Secure random UUID bytes also work on HTTP; the operation owner retains this key for retries. */
const submissionIdentity = () => {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** Protocol-only response shaping; backend owns input admission, settlement and Run progression. */
export const buildHostInputResponse = (input, { sessionId, currentRequest, userId }) => {
  assertRecord(input);
  assertJSONValue(input);
  if (Object.keys(input).some((key) => !RESPONSE_FIELDS.has(key))) throw new TypeError('Unsupported input response field');
  if (!currentRequest || !input.inputRequest) throw staleRequest();
  assertJSONValue(currentRequest);
  assertRequest(currentRequest);
  if (sessionId !== currentRequest.session_id
    || IDENTITY_FIELDS.some((key) => input.inputRequest[key] !== currentRequest[key])) throw staleRequest();
  assertText(userId);
  const response_kind = input.response_kind ?? 'answers';
  const buildResponse = RESPONSE_BUILDERS.get(response_kind);
  if (!buildResponse) throw new TypeError('Unsupported input response kind');
  const submission = {
    ...Object.fromEntries(IDENTITY_FIELDS.map((key) => [key, currentRequest[key]])),
    idempotency_key: submissionIdentity(),
    ...buildResponse(input, currentRequest.missing_inputs),
    submitted_by: userId,
    submitted_at: new Date().toISOString().replace('Z', '000Z'),
  };
  return {
    sessionId, runId: currentRequest.run_id, operationKey: 'send_message',
    input: { runId: currentRequest.run_id, parameters: { auto_input_submission: submission } },
    ...RESPONSE_ECHO.get(response_kind)(input),
  };
};
